import AppHeader from "@/components/AppHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import IsomerExplorer from "@/components/organic/IsomerExplorer";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

const functionalGroups = [
  { name: "Alkane", suffix: "-ane", formula: "C–C", example: "Ethane (C₂H₆)", desc: "Saturated hydrocarbons with single bonds only." },
  { name: "Alkene", suffix: "-ene", formula: "C=C", example: "Ethene (C₂H₄)", desc: "Contain at least one carbon–carbon double bond." },
  { name: "Alkyne", suffix: "-yne", formula: "C≡C", example: "Ethyne (C₂H₂)", desc: "Contain at least one carbon–carbon triple bond." },
  { name: "Alcohol", suffix: "-ol", formula: "R–OH", example: "Ethanol (C₂H₅OH)", desc: "Hydroxyl group attached to a carbon atom." },
  { name: "Aldehyde", suffix: "-al", formula: "R–CHO", example: "Methanal (HCHO)", desc: "Carbonyl group at the end of a carbon chain." },
  { name: "Ketone", suffix: "-one", formula: "R–CO–R'", example: "Propanone (CH₃COCH₃)", desc: "Carbonyl group between two carbon atoms." },
  { name: "Carboxylic acid", suffix: "-oic acid", formula: "R–COOH", example: "Ethanoic acid (CH₃COOH)", desc: "Carboxyl group; weakly acidic." },
  { name: "Ester", suffix: "-oate", formula: "R–COO–R'", example: "Ethyl ethanoate", desc: "Formed from a carboxylic acid + alcohol." },
  { name: "Amine", suffix: "-amine", formula: "R–NH₂", example: "Methylamine (CH₃NH₂)", desc: "Derivatives of ammonia; weakly basic." },
  { name: "Amide", suffix: "-amide", formula: "R–CONH₂", example: "Ethanamide (CH₃CONH₂)", desc: "Carbonyl bonded to a nitrogen atom." },
  { name: "Ether", suffix: "alkoxy-", formula: "R–O–R'", example: "Diethyl ether", desc: "Oxygen atom bonded to two carbon groups." },
  { name: "Haloalkane", suffix: "halo-", formula: "R–X", example: "Chloromethane (CH₃Cl)", desc: "Hydrogen replaced by a halogen (F, Cl, Br, I)." },
];

const namingRules = [
  { step: 1, rule: "Find the longest continuous carbon chain - this is the parent chain." },
  { step: 2, rule: "Identify the principal functional group; it determines the suffix." },
  { step: 3, rule: "Number the chain from the end giving the principal group the lowest locant." },
  { step: 4, rule: "Identify and number all substituents (alkyl groups, halogens, etc.)." },
  { step: 5, rule: "List substituents alphabetically, separated by hyphens, with locants." },
  { step: 6, rule: "Combine: locants–prefixes–parent–suffix (e.g., 2-methylbutan-1-ol)." },
];

const reactionTypes = [
  { type: "Substitution", example: "CH₄ + Cl₂ → CH₃Cl + HCl", desc: "An atom or group is replaced by another (common in alkanes & haloalkanes)." },
  { type: "Addition", example: "C₂H₄ + Br₂ → C₂H₄Br₂", desc: "Atoms add across a double or triple bond (alkenes/alkynes)." },
  { type: "Elimination", example: "C₂H₅OH → C₂H₄ + H₂O", desc: "A small molecule (water, HX) is removed, forming a double bond." },
  { type: "Combustion", example: "CH₄ + 2O₂ → CO₂ + 2H₂O", desc: "Hydrocarbon burns in oxygen, releasing energy." },
  { type: "Esterification", example: "CH₃COOH + C₂H₅OH ⇌ CH₃COOC₂H₅ + H₂O", desc: "Acid + alcohol → ester + water (acid-catalyzed)." },
  { type: "Oxidation", example: "CH₃CH₂OH → CH₃CHO → CH₃COOH", desc: "Primary alcohol → aldehyde → carboxylic acid." },
];

export default function OrganicChemistry() {
  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Organic Chemistry Tools — ElementPad" description="Explore organic molecules in 3D, view skeletal structures, and learn functional group chemistry." path="/organic" />
      <AppHeader />
      <Main className="py-6">
        <div className="max-w-[1200px] mx-auto px-4">
          <header className="mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-1">Organic Chemistry</h1>
            <p className="text-sm text-muted-foreground">
              Explore functional groups, IUPAC naming, and key reaction types of carbon compounds.
            </p>
          </header>

          <Tabs defaultValue="groups" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="groups">Functional Groups</TabsTrigger>
              <TabsTrigger value="naming">IUPAC Naming</TabsTrigger>
              <TabsTrigger value="reactions">Reaction Types</TabsTrigger>
              <TabsTrigger value="isomers">Isomer Explorer</TabsTrigger>
            </TabsList>

            <TabsContent value="groups">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {functionalGroups.map((g) => (
                  <Card key={g.name}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{g.name}</CardTitle>
                        <Badge variant="secondary" className="font-mono text-xs">{g.suffix}</Badge>
                      </div>
                      <CardDescription className="font-mono text-foreground">{g.formula}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-1">
                      <p>{g.desc}</p>
                      <p className="text-xs"><span className="text-foreground font-medium">Example:</span> {g.example}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="naming">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">IUPAC Naming Steps</CardTitle>
                  <CardDescription>Systematic rules for naming organic compounds.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3">
                    {namingRules.map((r) => (
                      <li key={r.step} className="flex gap-3">
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/15 text-primary text-sm font-semibold flex items-center justify-center">
                          {r.step}
                        </span>
                        <span className="text-sm text-foreground pt-0.5">{r.rule}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-6 p-4 rounded-md border border-border bg-muted/30">
                    <p className="text-xs text-muted-foreground mb-1">Worked example</p>
                    <p className="font-mono text-sm text-foreground">CH₃–CH(CH₃)–CH₂–OH → 2-methylpropan-1-ol</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="reactions">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {reactionTypes.map((r) => (
                  <Card key={r.type}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">{r.type}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <p className="font-mono text-xs bg-muted/40 px-2 py-1.5 rounded text-foreground">{r.example}</p>
                      <p className="text-muted-foreground">{r.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="isomers">
              <IsomerExplorer />
            </TabsContent>
          </Tabs>
        </div>
      </Main>
    </div>
  );
}
